import React, { useRef, useMemo } from "react";

import { motion, useTransform } from "framer-motion";
import { useScroll } from "framer-motion";
import { FaExternalLinkAlt } from 'react-icons/fa';
import SectionHeading from "./section-heading";
import { PROJECTS_DATA } from "../constants";
import { useIsMobileView, useSectionInView } from "../lib/hooks";

type ProjectProps = (typeof PROJECTS_DATA)[number];

const limitTags = 5;

const Project = ({
  title,
  description,
  tags,
  imageUrl,
  projectUrl,
}: ProjectProps) => {
  const projectRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobileView();

  const { scrollYProgress } = useScroll({
    target: projectRef,
    offset: ["0 1", "1.33 1"],
  });

  const scaleProgress = useTransform(scrollYProgress, [0, 1], [0.8, 1]);
  const opacityProgress = useTransform(scrollYProgress, [0, 1], [0.6, 1]);

  const tagsToShow = useMemo(() => {
    if (isMobile) {
      return [...tags];
    }
    return [...tags].slice(0, limitTags);
  }, [tags, isMobile]);

  const remainingTags = useMemo(() => {
    if (tags.length <= limitTags || isMobile) {
      return [];
    }
    const remaining = [...tags];
    remaining.splice(0, limitTags);
    return remaining;
  }, [tags, isMobile]);

  return (
    <motion.article
      style={{
        scale: scaleProgress,
        opacity: opacityProgress,
      }}
      ref={projectRef}
      className="group mb-3 sm:mb-8 last:mb-0"
    >
      <div className="bg-gray-100 max-w-[80rem] xl:max-w-[90%] xl:ml-[5%] xl:mb-4 md:group-even:pl-8 border border-black/5 overflow-hidden md:pr-8 relative md:h-[20rem] rounded-lg hover:bg-gray-200 transition dark:bg-white/10 dark:hover:bg-white/20">
        <div className="pt-4 pb-7 px-5 md:pl-10 md:pr-2 md:pt-10 md:max-w-[50%] flex flex-col h-full md:group-even:ml-[18rem]">
          <h3 className="text-2xl font-semibold hidden md:block">{title}</h3>

          <h3 className="text-2xl font-semibold block md:hidden">
            <a
              href={projectUrl}
              target="_blank"
              rel="noreferrer noopener"
              title="Go to Project"
              className="flex"
            >
              {title}
              <FaExternalLinkAlt className="text-xs ml-1 text-blue-500" />
            </a>
          </h3>
          <p className="mt-2 leading-relaxed text-gray-700 dark:text-white/70">
            {description}
          </p>

          <ul className="flex flex-wrap mt-4 gap-2 md:mt-auto">
            {tagsToShow.map((tag, i) => (
              <li
                key={`${title}-tags-${i}`}
                className="bg-black/[0.7] px-3 py-1 text-[0.7rem] uppercase tracking-wider text-white rounded-full dark:text-white/70"
              >
                {tag}
              </li>
            ))}
            {remainingTags.length > 0 && (
              <li className="bg-black/[0.7] px-3 py-1 text-[0.7rem] uppercase tracking-wider text-white rounded-full dark:text-white/70 cursor-pointer">
                +{remainingTags.length}
              </li>
            )}
          </ul>
        </div>
        <a
          href={projectUrl}
          target="_blank"
          rel="noreferrer noopener"
          title="Go to Project"
        >
          <img
            src={imageUrl}
            alt={title}
            // quality={95}
            className="absolute hidden md:block top-8 -right-40 w-[28.25rem]
            rounded-t-lg shadow-2xl group-even:right-[initial] 
            group-even:-left-40 group-hover:-translate-x-3
            group-hover:translate-y-3 group-hover:-rotate-2
            
            group-even:group-hover:translate-x-3
            group-even:group-hover:translate-y-3 group-even:group-hover:rotate-2
            
            group-hover:scale-[1.04] transition"
          />
        </a>
      </div>
    </motion.article>
  );
};

// Define the Projects component to display a list of projects.
const Projects = () => {
  // Use the 'useSectionInView' hook to track section visibility.
  const { ref } = useSectionInView("Projects", 0.5);

  return (
    <section ref={ref} id="projects" className="scroll-mt-28 mb-28">
      <SectionHeading>My projects</SectionHeading>
      <div className="sm xl:grid xl:grid-cols-2">
        {PROJECTS_DATA.map((project, i) => (
          <Project key={`project-${i}`} {...project} />
        ))}
      </div>
    </section>
  );
};

export default Projects;
